import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEpicRequestsPaged } from '@/hooks/queries/use-epic-requests';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { usePageTitle } from '@/hooks/use-page-title';
import { useIsMobile } from '@/hooks/use-mobile';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, ChevronDown, Lightbulb, User } from 'lucide-react';
import { PageInfoButton } from '@/components/common/page-info-button';
import { RequestsScopeFilter } from '@/components/epic-requests/requests-scope-filter';
import type { ScopeMode } from '@/components/common/scope-selector';
import { SmartEmptyState } from '@/components/common/smart-empty-state';
import { cn } from '@/lib/utils';
import { ComplianceBadge } from '@/components/ui/compliance-badge';
import type { EpicRequestStatus, EpicRequestFilters } from '@/lib/api/epic-requests';
import type { EpicRequestWithReactionCounts } from '@/lib/api/epic-requests';
import { EntityCard } from '@/components/ui/entity-card';
import { SortControl } from '@/components/common/sort-control';
import type { SortOption } from '@/components/common/sort-control';
import { PaginationControls, PAGE_SIZE_OPTIONS } from '@/components/common/pagination-controls';
import { getSortPreference, setSortPreference } from '@/lib/sort-preferences';

// Status badge colors based on status
const statusColors: Record<EpicRequestStatus, string> = {
  pending: 'bg-yellow-900/30 text-yellow-300',
  approved: 'bg-green-900/30 text-green-300',
  rejected: 'bg-red-900/30 text-red-300',
  converted: 'bg-blue-900/30 text-blue-300',
  planning: 'bg-purple-900/30 text-purple-300',
};

// Status order for grouping
const statusOrder: EpicRequestStatus[] = ['planning', 'pending', 'approved', 'converted', 'rejected'];

// Status hue colors for card glow
const statusHue: Record<EpicRequestStatus, string> = {
  pending: '#EAB308',
  approved: '#22C55E',
  rejected: '#EF4444',
  converted: '#3B82F6',
  planning: '#8B5CF6',
};

interface RequestCardProps {
  request: EpicRequestWithReactionCounts;
}

function RequestCard({ request }: RequestCardProps) {
  const hueColor = statusHue[request.status];
  const isPersonalScope = !!request.personalScopeId;

  const hoursAgo = Math.floor((Date.now() - new Date(request.createdAt).getTime()) / 3_600_000);
  const timeLabel = hoursAgo < 1 ? 'just now' : hoursAgo < 24 ? `${String(hoursAgo)} hour${hoursAgo === 1 ? '' : 's'} ago` : `${String(Math.floor(hoursAgo / 24))} day${Math.floor(hoursAgo / 24) === 1 ? '' : 's'} ago`;

  return (
    <EntityCard
      href={`/epic-requests/${request.id}`}
      hueColor={hueColor}
      icon={
        <Lightbulb className="h-5 w-5" style={{ color: hueColor }} />
      }
      title={request.identifier ? `${request.identifier} — ${request.title}` : request.title}
      subtitle={request.description ?? undefined}
      badge={isPersonalScope ? (
        <Badge className="gap-1 text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06]">
          <User className="h-3 w-3" />
          Personal
        </Badge>
      ) : undefined}
      user={request.requestedBy ? { name: request.requestedBy.name, avatarUrl: request.requestedBy.avatarUrl ?? null } : undefined}
      meta={`Submitted ${timeLabel}`}
      footer={
        <ComplianceBadge score={request.complianceScore ?? null} interactive={false} />
      }
    />
  );
}

const REQUEST_SORT_OPTIONS: SortOption[] = [
  { value: 'createdAt', label: 'Newest', direction: 'desc' },
  { value: 'createdAt', label: 'Oldest', direction: 'asc' },
  { value: 'updatedAt', label: 'Last updated', direction: 'desc' },
  { value: 'title', label: 'Title A-Z', direction: 'asc' },
];

const DEFAULT_REQUEST_SORT = { orderBy: 'createdAt', orderDirection: 'desc' as const };

export function EpicRequestsPage() {
  usePageTitle("Requests");
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<EpicRequestStatus[]>(['pending', 'approved']);

  // Sort state backed by localStorage
  const [sort, setSort] = useState(() =>
    getSortPreference('requestsSortPreference', DEFAULT_REQUEST_SORT, ['createdAt', 'updatedAt', 'title']),
  );

  const handleSortChange = (orderBy: string, orderDirection: 'asc' | 'desc') => {
    const newSort = { orderBy, orderDirection };
    setSort(newSort);
    setSortPreference('requestsSortPreference', newSort);
    setCursorStack([undefined]); // Reset to page 1
  };

  // Page size backed by localStorage
  const [pageSize, setPageSize] = useState(() => {
    try {
      const stored = localStorage.getItem('requestsPageSize');
      const parsed = stored ? parseInt(stored, 10) : NaN;
      return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 20;
    } catch { return 20; }
  });

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCursorStack([undefined]); // Reset to page 1
    try { localStorage.setItem('requestsPageSize', String(size)); } catch { /* quota/security */ }
  }, []);

  // Cursor stack for page-by-page navigation
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const currentPage = cursorStack.length;
  const currentCursor = cursorStack[cursorStack.length - 1];

  // Initialize scope from URL params (userId) or localStorage with 'my' as default
  const [searchParams, setSearchParams] = useSearchParams();
  const urlUserId = searchParams.get('userId');

  const VALID_SCOPES: ScopeMode[] = ['my', 'all', 'team', 'user'];
  const [scope, setScope] = useState<ScopeMode>(() => {
    if (urlUserId) return 'user';
    const stored = localStorage.getItem('requestsScope') as ScopeMode | null;
    return stored && VALID_SCOPES.includes(stored) ? stored : 'my';
  });

  const [scopeId, setScopeId] = useState<string | undefined>(() => {
    if (urlUserId) return urlUserId;
    const stored = localStorage.getItem('requestsScopeId');
    return stored && stored !== '' ? stored : undefined;
  });

  const currentUser = useAuthStore((state) => state.user);

  // Sync scope when URL userId param changes while mounted
  useEffect(() => {
    if (urlUserId) {
      if (urlUserId !== scopeId || scope !== 'user') {
        setScope('user');
        setScopeId(urlUserId);
        setCursorStack([undefined]);
      }
    } else if (!urlUserId && scope === 'user' && scopeId) {
      // URL param removed — restore persisted default
      const stored = localStorage.getItem('requestsScope') as ScopeMode | null;
      setScope(stored && VALID_SCOPES.includes(stored) ? stored : 'my');
      setScopeId(localStorage.getItem('requestsScopeId') || undefined);
      setCursorStack([undefined]);
    }
  }, [urlUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScopeChange = (newScope: ScopeMode, newScopeId?: string) => {
    setScope(newScope);
    setScopeId(newScopeId);
    setCursorStack([undefined]); // Reset to page 1

    // Clear URL userId param if present
    if (searchParams.has('userId')) {
      searchParams.delete('userId');
      setSearchParams(searchParams, { replace: true });
    }

    localStorage.setItem('requestsScope', newScope);
    localStorage.setItem('requestsScopeId', newScopeId ?? '');
  };

  // Build filter parameters based on scope
  const requestFilters = useMemo(() => {
    const filters: EpicRequestFilters = {};

    if (scope === 'my' && currentUser?.id) {
      filters.requestedById = currentUser.id;
    } else if (scope === 'user' && scopeId) {
      filters.requestedById = scopeId;
    }
    // 'all' scope: no requestedById filter (show everything)
    // 'team' scope: not yet supported — treated same as 'all' until API supports team filtering

    filters.orderBy = sort.orderBy as EpicRequestFilters['orderBy'];
    filters.orderDirection = sort.orderDirection;
    filters.limit = pageSize;
    if (currentCursor) {
      filters.cursor = currentCursor;
    }

    return filters;
  }, [scope, scopeId, currentUser?.id, sort, pageSize, currentCursor]);

  const { data, isLoading, isFetching } = useEpicRequestsPaged(requestFilters);

  // Extract page data and apply client-side filtering (status + search)
  const pageRequests = data?.data ?? [];
  const totalCount = data?.meta?.totalCount;
  const nextCursor = data?.meta?.cursor;
  const hasNextPage = Boolean(data?.meta?.hasMore && nextCursor);
  const hasPreviousPage = currentPage > 1;
  const totalPages = totalCount != null ? Math.ceil(totalCount / pageSize) : undefined;

  const handleNextPage = useCallback(() => {
    if (hasNextPage && nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  }, [hasNextPage, nextCursor]);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);
  
  // Filter by selected statuses (if any selected)
  // "planning" ERs are always visible — it's a transient active state, not a user filter
  const statusFilteredRequests = selectedStatuses.length > 0
    ? pageRequests.filter((req) => req.status === 'planning' || selectedStatuses.includes(req.status))
    : pageRequests;
  
  // Then filter by search query
  const filteredRequests = searchQuery
    ? statusFilteredRequests.filter((req) =>
        req.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : statusFilteredRequests;

  // Group requests by status
  const groupedRequests = filteredRequests.reduce<Partial<Record<EpicRequestStatus, EpicRequestWithReactionCounts[]>>>(
    (acc, request) => {
      const group = acc[request.status] ?? [];
      group.push(request);
      acc[request.status] = group;
      return acc;
    },
    {}
  );

  return (
    <div className="pt-4">
      <div className="glass-card rounded-2xl p-4 md:p-6">
      {/* Page title + Filters row on desktop */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Requests</h1>
          <PageInfoButton title="Requests" />
        </div>

        {/* Filters — stacked vertically on mobile, row on desktop */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          {/* Row 1: scope filter */}
          <div className="flex items-center gap-2">
          <RequestsScopeFilter
            scope={scope}
            {...(scopeId !== undefined && { scopeId })}
            onScopeChange={handleScopeChange}
          />
          </div>

        {/* Row 2: status filter + sort control */}
        <div className="flex items-center gap-2">
        <div data-help-id="epicRequests.statusFilter">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className={cn("justify-between font-normal", isMobile ? "w-full" : "w-[220px]")}
            >
              {selectedStatuses.length === 0
                ? 'All statuses'
                : selectedStatuses.length === 1 && selectedStatuses[0]
                ? `${selectedStatuses[0].charAt(0).toUpperCase()}${selectedStatuses[0].slice(1)}`
                : `${String(selectedStatuses.length)} statuses selected`}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[220px] p-3" align="start">
            <div className="space-y-2" role="presentation" onClick={(e) => { e.stopPropagation(); }}>
              {(['pending', 'approved', 'converted', 'rejected'] as EpicRequestStatus[]).map((status) => (
                <label
                  key={status}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-accent p-2 rounded-md"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Checkbox
                    checked={selectedStatuses.includes(status)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedStatuses([...selectedStatuses, status]);
                      } else {
                        setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
                      }
                    }}
                  />
                  <span className="text-sm capitalize">
                    {status}
                  </span>
                </label>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
        <SortControl
          sortOptions={REQUEST_SORT_OPTIONS}
          activeSort={sort}
          onSortChange={handleSortChange}
        />
        </div>
        <div data-help-id="epicRequests.search" className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            className="pl-9 md:pl-9"
          />
        </div>
        </div>
      </div>

      {/* Request list */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : filteredRequests.length === 0 ? (
        !searchQuery && !totalCount ? (
          <SmartEmptyState
            icon={Lightbulb}
            title="No epic requests yet"
            description="Epic requests are proposals for new work. Use the CLI to submit your first idea."
            ctaLabel="Learn more"
            accent="blue"
          />
        ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? 'No requests match your search'
              : scope !== 'all'
                ? 'No requests found for this filter. Try changing your filter selection or creating a new request.'
                : 'No requests yet'}
          </p>

        </div>
        )
      ) : (
        <>
          {/* Grouped by status */}
          <div className="relative space-y-8">
            {statusOrder.map((status) => {
              const requestsInGroup = groupedRequests[status] ?? [];
              
              // Skip empty groups
              if (requestsInGroup.length === 0) {
                return null;
              }

              return (
                <div key={status}>
                  {/* Group header with status badge and count */}
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      className={cn(
                        'text-xs font-medium',
                        statusColors[status]
                      )}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                  </div>

                  {/* Grid of cards for this status group */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {requestsInGroup.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Subtle loading overlay when navigating between pages */}
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-background/30 rounded-2xl pointer-events-none" />
            )}
          </div>

          {/* Pagination controls */}
          <PaginationControls
            showing={filteredRequests.length}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            totalPages={totalPages}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            isLoading={isFetching}
          />
        </>
      )}
      </div>
    </div>
  );
}
